import {HttpResponse, HttpErrorResponse} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import {GroupContext} from 'app/models/group-context.model';
import {hasServerErrorCode} from 'app/services/server-error-code';
import {groupSettingsErrorKey} from 'app/services/operation-conflict';
import {Component, OnInit, OnDestroy, AfterViewInit, AfterViewChecked, ElementRef, Renderer2, Injector} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Group} from '../../../../../models/group.model';
import {FullUser} from '../../../../../models/full-user.model';
import {GroupUser} from '../../../../../models/group-user.model';
import {AdminService} from '../../../../../services/admin.service';
import { User } from '../../../../../models/user.model';
import { UserService } from '../../../../../services/user.service';
import {HeadReadiness, HeadTraining} from 'app/models/head-readiness.model';

const HEAD_HTTP_OK = 200;
const HEAD_HTTP_CONFLICT = 409;
const HEAD_REVISION_CONFLICT = 'USER_REVISION_CONFLICT';
const HEAD_CONFLICT_KEY = 'GROUP_HEADS_REVISION_CONFLICT';
const HEAD_RELOAD_FAILED = 'GROUP_SETTINGS_RELOAD_FAILED';
const HEAD_EMPTY_TEXT = '';
const HEAD_BOOLEAN_TYPE = 'boolean';
const HEAD_ROLE = 'ROLE_HEAD';
const ADMIN_ROLE = 'ROLE_ADMIN';
const USER_ROLE = 'ROLE_USER';
const FIRST_ROLE_INDEX = 0;
const HEAD_PREPARATION_CANCELLED = 'CANCELLED';
const HEAD_PREPARATION_SKIPPED = 'NOT_REQUIRED';
const HEAD_PREPARATION_ASSIGNED = 'ASSIGNED';
const HEAD_HINT_PREFIX = 'HEAD_PREPARATION_HINT_';
const HEAD_PREPARATION_NONE = 'NONE';
const HEAD_MODAL_TITLE = 'head-appointments-title';
const HEAD_DIALOG_ROLE = 'dialog';
const HEAD_ROLE_ATTRIBUTE = 'role';
const HEAD_LABEL_ATTRIBUTE = 'aria-labelledby';
const HEAD_EMPTY_TRAINING_COUNT = 0;
const HEAD_TAB_KEY = 'Tab';
const HEAD_KEYDOWN_EVENT = 'keydown';
const HEAD_FOCUSABLE_SELECTOR = 'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]';
const HEAD_FIRST_CONTROL_INDEX = 0;
const HEAD_LAST_CONTROL_OFFSET = 1;
const HEAD_DEPARTMENT_TYPE = 'DEPARTMENT';
const HEAD_SERVER_ERROR = 500;
const HEAD_UNKNOWN_RESULT = 'HEAD_ASSIGNMENT_UNCONFIRMED';

export interface PendingHeadAssignment {
  actorId: string;
  groupId: string;
  payload?: {headId: string, deputyId: string, taskNotRequired: boolean};
  head?: FullUser;
  deputy?: FullUser;
}

@Component({
  selector: 'app-set-head-deputy',
  templateUrl: './set-head-deputy.component.html',
  styleUrls: ['./set-head-deputy.component.css']
})
export class SetHeadDeputyComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  errorKey = HEAD_EMPTY_TEXT;
  requiresReload = true;
  canManageSettings = false;
  canAssignHead = false;
  didSave = false;
  pendingAssignment: PendingHeadAssignment;
  private readonly destroy$ = new Subject<void>();
  private dialog: HTMLElement;
  private removeDialogKeydown: () => void;
  public activeModal: NgbActiveModal;
  private element: ElementRef;
  private renderer: Renderer2;
  public group: Group;
  public selectedHead: FullUser;
  public selectedDeputy: FullUser;
  public activeUsers: GroupUser[];
  public users: FullUser[] = [];
  public isLoaded = false;
  public errorInfo = false;
  public successInfo = false;
  public noSelected = false;
  readiness: HeadReadiness;
  readinessLoading = false;
  readinessError = false;
  taskSubmitting = false;
  taskNotRequired = false;
  taskError = false;
  showTrainingDetails = false;
  user: User;
	isAdmin = false;
	isHeadOfGroup = false;
	isUser = false;
	isDeputy = false;

  constructor(public adminService: AdminService,
              public userService: UserService,
              injector: Injector) {
    this.activeModal = injector.get(NgbActiveModal);
    this.element = injector.get(ElementRef);
    this.renderer = injector.get(Renderer2);
  }

  public showHeadFields = true;

  ngOnInit(): void {
    this.user = this.userService.getLocal();
    this.isAdmin = this.user.roles.indexOf(ADMIN_ROLE) >= FIRST_ROLE_INDEX;
    this.isHeadOfGroup = this.user.roles.indexOf(HEAD_ROLE) >= FIRST_ROLE_INDEX;
    this.isUser = this.user.roles.indexOf(USER_ROLE) >= FIRST_ROLE_INDEX;
    this.reloadGroup();
  }

  ngOnDestroy(): void {
    if (this.removeDialogKeydown) { this.removeDialogKeydown(); }
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    let parent = this.renderer.parentNode(this.element.nativeElement);
    while (parent && parent.getAttribute && parent.getAttribute(HEAD_ROLE_ATTRIBUTE) !== HEAD_DIALOG_ROLE) {
      parent = this.renderer.parentNode(parent);
    }
    if (!parent || !parent.getAttribute) { return; }
    this.dialog = parent;
    this.renderer.setAttribute(this.dialog, HEAD_LABEL_ATTRIBUTE, HEAD_MODAL_TITLE);
    this.removeDialogKeydown = this.renderer.listen(this.dialog, HEAD_KEYDOWN_EVENT, event => this.trapDialogFocus(event));
  }

  ngAfterViewChecked(): void {
    if (this.dialog && this.dialog.ownerDocument.activeElement === this.dialog.ownerDocument.body) { this.dialog.focus(); }
  }

  private trapDialogFocus(event: KeyboardEvent): void {
    if (event.key !== HEAD_TAB_KEY) { return; }
    const controls: HTMLElement[] = Array.prototype.slice.call(this.dialog.querySelectorAll(HEAD_FOCUSABLE_SELECTOR));
    const visible = controls.filter(control => control.tabIndex >= HEAD_FIRST_CONTROL_INDEX && !!control.getClientRects().length);
    const first = visible[HEAD_FIRST_CONTROL_INDEX];
    const last = visible[visible.length - HEAD_LAST_CONTROL_OFFSET];
    const active = this.dialog.ownerDocument.activeElement;
    if (!first || active === this.dialog || (event.shiftKey ? active === first : active === last)) {
      event.preventDefault();
      (event.shiftKey ? last || this.dialog : first || this.dialog).focus();
    }
  }

  reloadGroup(): void {
    if (!this.group || (this.isLoaded === false && !this.requiresReload)) { return; }
    this.isLoaded = false;
    this.canAssignHead = false;
    this.requiresReload = false;
    const id = this.group.id;
    Observable.forkJoin(this.adminService.getGroupByID(id) as Observable<HttpResponse<Group>>, this.adminService.getGroupContext(id))
      .takeUntil(this.destroy$).subscribe(result => this.acceptGroupReload(result), () => {
        this.isLoaded = this.requiresReload = true;
        this.errorKey = HEAD_RELOAD_FAILED;
      });
  }

  private acceptGroupReload(result: [HttpResponse<Group>, HttpResponse<GroupContext>]): void {
    const [response, contextResponse] = result;
    const context = contextResponse.body;
    if (!this.validGroup(response) || contextResponse.status !== HEAD_HTTP_OK || !context ||
      context.groupId !== this.group.id || typeof context.canManageGroupSettings !== HEAD_BOOLEAN_TYPE) {
      this.isLoaded = this.requiresReload = true;
      this.errorKey = HEAD_RELOAD_FAILED; return;
    }
    this.canManageSettings = context.canManageGroupSettings || context.canAssignHead === true;
    this.canAssignHead = context.canAssignHead === true;
    this.applyGroup(response.body);
    this.requiresReload = this.successInfo = false;
    this.errorKey = this.hasPendingAssignment ? HEAD_UNKNOWN_RESULT : HEAD_EMPTY_TEXT;
  }

  private validGroup(response: HttpResponse<Group>): boolean {
    return response.status === HEAD_HTTP_OK && !!response.body && response.body.id === this.group.id &&
      Array.isArray(response.body.activeUsers);
  }

  private applyGroup(group: Group): void {
    this.group = group;
    this.activeUsers = group.activeUsers;
    this.users = this.activeUsers.map(member => member.user);
    this.selectedHead = this.hasPendingAssignment ? this.pendingAssignment.head : group.head;
    this.selectedDeputy = this.hasPendingAssignment ? this.pendingAssignment.deputy : group.deputy;
    this.taskNotRequired = false;
    this.taskError = this.showTrainingDetails = false;
    this.isLoaded = true;
    this.errorInfo = this.noSelected = false;
    this.loadReadiness();
  }

  setShowHeadFields(value: boolean) {
    this.showHeadFields = value;
  }

  setGroup(group: Group) {
    this.group = group;
  }

  selectedH(user: FullUser) {
    if (!this.isLoaded || this.requiresReload || this.taskSubmitting || this.hasPendingAssignment) { return; }
    this.selectedHead = user;
    this.taskNotRequired = false;
    this.taskError = this.showTrainingDetails = false;
    this.loadReadiness();
  }

  loadReadiness(): void {
    if (this.taskSubmitting || this.isSubdepartment || this.hasPendingAssignment) { return; }
    if (!this.selectedHead || !this.readiness || this.readiness.userId !== this.selectedHead.id) { this.readiness = null; }
    this.readinessError = false;
    this.readinessLoading = false;
    if (!this.canAssignHead || !this.selectedHead) { return; }
    const userId = this.selectedHead.id;
    this.readinessLoading = true;
    this.adminService.getHeadReadiness(this.group.id, userId)
      .takeUntil(this.destroy$).subscribe(readiness => {
        if (!this.selectedHead || this.selectedHead.id !== userId) { return; }
        this.readiness = readiness;
        this.readinessLoading = false;
        this.taskError = false;
      }, () => {
        if (!this.selectedHead || this.selectedHead.id !== userId) { return; }
        this.readinessLoading = false;
        this.readinessError = true;
      });
  }

  startPreparation(): void {
    if (!this.showStartPreparation || this.taskSubmitting || this.readinessLoading || this.readinessError || this.taskError) { return; }
    const userId = this.selectedHead.id;
    this.taskSubmitting = true;
    this.readinessError = false;
    this.adminService.startHeadPreparation(this.group.id, userId)
      .takeUntil(this.destroy$).subscribe(readiness => {
        this.taskSubmitting = false;
        if (this.selectedHead && this.selectedHead.id === userId) { this.readiness = readiness; }
      }, () => {
        this.taskSubmitting = false;
        this.taskError = true;
      });
  }

  get changesHead(): boolean {
    return !!this.selectedHead && (!this.group.head || this.group.head.id !== this.selectedHead.id);
  }

  get isSubdepartment(): boolean {
    return !!this.group && this.group.groupType === HEAD_DEPARTMENT_TYPE && !!this.group.parentDepartmentId;
  }

  get hasPendingAssignment(): boolean {
    return !!this.pendingAssignment && !!this.pendingAssignment.payload;
  }

  get canSave(): boolean {
    if (!this.isLoaded || this.requiresReload || !this.canManageSettings || this.taskSubmitting || this.hasPendingAssignment) { return false; }
    if (this.isSubdepartment || !this.canAssignHead || !this.changesHead) { return true; }
    return !!this.readiness && !this.readinessLoading && !this.readinessError && !this.taskError &&
      (this.readiness.canAssign || this.taskNotRequired && this.readiness.canSkipPreparation);
  }

  get completedTrainingCount(): number {
    return this.readiness ? this.readiness.trainings.filter(training => training.completedLevel >= training.requiredLevel).length : HEAD_EMPTY_TRAINING_COUNT;
  }

  get showStartPreparation(): boolean {
    if (this.isSubdepartment || this.hasPendingAssignment || !this.readiness || !this.readiness.canStartPreparation) { return false; }
    const status = this.readiness.preparationStatus;
    return !status || status === HEAD_PREPARATION_CANCELLED || status === HEAD_PREPARATION_SKIPPED || status === HEAD_PREPARATION_ASSIGNED;
  }

  get preparationHintKey(): string {
    return HEAD_HINT_PREFIX + (this.readiness && this.readiness.preparationStatus || HEAD_PREPARATION_NONE);
  }

  trackTraining(index: number, training: HeadTraining): string {
    return training.specializationId;
  }

  trackUser(index: number, user: FullUser): string { return user.id; }

  selectedD(user: FullUser) {
    if (!this.isLoaded || this.requiresReload || this.taskSubmitting || this.hasPendingAssignment) { return; }
    this.selectedDeputy = user;
  }

  setHeadDeputy(): void {
    if (!this.canSave) { return; }
    this.noSelected = !this.selectedHead && !this.selectedDeputy;
    if (this.noSelected) { return; }
    const headId = this.selectedHead ? this.selectedHead.id : null;
    const deputyId = this.selectedDeputy ? this.selectedDeputy.id : null;
    if (!this.pendingAssignment) { this.pendingAssignment = {actorId: this.user.id, groupId: this.group.id}; }
    this.pendingAssignment.payload = Object.freeze({headId: headId, deputyId: deputyId,
      taskNotRequired: !this.isSubdepartment && this.changesHead && this.taskNotRequired});
    this.pendingAssignment.head = this.selectedHead;
    this.pendingAssignment.deputy = this.selectedDeputy;
    this.retryAssignment();
  }

  retryAssignment(): void {
    if (!this.hasPendingAssignment || !this.isLoaded || this.taskSubmitting || this.requiresReload || !this.canManageSettings) { return; }
    const currentUser = this.userService.getLocal();
    if (!currentUser || this.pendingAssignment.actorId !== currentUser.id || this.pendingAssignment.groupId !== this.group.id) { return; }
    this.isLoaded = this.successInfo = false;
    this.adminService.setHeadsForGroup(this.pendingAssignment.groupId, this.pendingAssignment.payload)
      .takeUntil(this.destroy$).subscribe(response => {
        if (!this.validGroup(response as HttpResponse<Group>)) { this.rejectSave(); return; }
        this.clearPendingAssignment();
        this.applyGroup(response.body as Group);
        this.successInfo = this.didSave = true;
        this.errorKey = HEAD_EMPTY_TEXT;
      }, error => this.rejectSave(error));
  }

  private rejectSave(error?: HttpErrorResponse): void {
    this.isLoaded = true;
    this.successInfo = false;
    if (!error || !error.status || error.status >= HEAD_SERVER_ERROR) {
      this.errorKey = HEAD_UNKNOWN_RESULT;
      return;
    }
    this.clearPendingAssignment();
    this.requiresReload = true;
    this.errorKey = error && error.status === HEAD_HTTP_CONFLICT && hasServerErrorCode(error, HEAD_REVISION_CONFLICT)
      ? HEAD_CONFLICT_KEY : groupSettingsErrorKey(error);
  }

  private clearPendingAssignment(): void {
    this.pendingAssignment.payload = null;
    this.pendingAssignment.head = this.pendingAssignment.deputy = null;
  }

  get sortedUsers(): FullUser[] {
    return this.users.sort((a, b) => (a.firstName || HEAD_EMPTY_TEXT).localeCompare(b.firstName || HEAD_EMPTY_TEXT));
  }

}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/popups/set-head-deputy/set-head-deputy.component.ts